import { neighboursOf, type MapSide } from "../document/layout.ts";
import type { MapDocument, WorldDocument } from "../document/types.ts";

/**
 * The neighbours' edge strips and the border band (P1.6-T144, ADR-109 decisions 6, 24 and 25): for a map with
 * placed neighbours, where each neighbour's edge strip is drawn outside the map's edge, which tiles of the
 * neighbour it shows, where the border band is tinted on the map's own side, which stretches of an edge no map
 * covers, and where Go to neighbour lands. The depth of both is one chunk, the unit the travel model
 * preloads, and is not the camera-stop band's, which is half the game's view and differs by axis. Tile units
 * throughout, in the map's own numbering from 0 at its top-left tile, so a strip outside the map has a
 * negative or past-the-edge position. Pure; view state, never saved or compiled.
 */

/**
 * A rectangle in tiles: its top-left tile and its size.
 */
export interface StripRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Whether a neighbour holds nothing, holds layers and is not marked ready, or is ready.
 */
export type StripState = "blank" | "draft" | "ready";

/**
 * One neighbour's strip along one side of a map.
 */
export interface EdgeStrip {
  readonly side: MapSide;
  readonly map: string;
  readonly name: string;
  readonly region: string | null;
  readonly state: StripState;
  readonly from: number;
  readonly to: number;
  readonly outside: StripRect;
  readonly source: StripRect;
  readonly band: StripRect;
}

/**
 * A stretch of a map's side that no placed neighbour covers: sea or a gap, marked "no map".
 */
export interface NoMapStretch {
  readonly side: MapSide;
  readonly from: number;
  readonly to: number;
  readonly outside: StripRect;
}

/**
 * What the guide draws around a map's edges.
 */
export interface EdgeStrips {
  readonly depth: number;
  readonly strips: readonly EdgeStrip[];
  readonly noMap: readonly NoMapStretch[];
}

/**
 * Where Go to neighbour lands: the neighbour, the side of it that faces the map, and the tile on that edge.
 */
export interface NeighbourTarget {
  readonly map: string;
  readonly side: MapSide;
  readonly x: number;
  readonly y: number;
}

const SIDES: readonly MapSide[] = ["north", "east", "south", "west"];

/**
 * The side a neighbour on a given side faces back with.
 *
 * @param side The side of the map.
 * @returns The opposite side.
 */
function opposite(side: MapSide): MapSide {
  return side === "north" ? "south" : side === "south" ? "north" : side === "east" ? "west" : "east";
}

/**
 * A map's length along a side: its width for north and south, its height for east and west.
 *
 * @param map The map.
 * @param side The side.
 * @returns The length in tiles.
 */
function lengthAlong(map: MapDocument, side: MapSide): number {
  return side === "north" || side === "south" ? map.width : map.height;
}

/**
 * A map's extent across a side: its height for north and south, its width for east and west.
 *
 * @param map The map.
 * @param side The side.
 * @returns The extent in tiles.
 */
function extentAcross(map: MapDocument, side: MapSide): number {
  return side === "north" || side === "south" ? map.height : map.width;
}

/**
 * The rectangle outside a map's side over a stretch of it.
 *
 * @param map The map.
 * @param side The side.
 * @param from The stretch's start along the side.
 * @param to Its end.
 * @param depth How far out.
 * @returns The rectangle.
 */
function outsideOf(map: MapDocument, side: MapSide, from: number, to: number, depth: number): StripRect {
  const span = to - from;
  switch (side) {
    case "north":
      return { x: from, y: -depth, width: span, height: depth };
    case "south":
      return { x: from, y: map.height, width: span, height: depth };
    case "east":
      return { x: map.width, y: from, width: depth, height: span };
    case "west":
      return { x: -depth, y: from, width: depth, height: span };
  }
}

/**
 * The rectangle inside a map's side over a stretch of it, the border band.
 *
 * @param map The map.
 * @param side The side.
 * @param from The stretch's start along the side.
 * @param to Its end.
 * @param depth How far in, at most the map's own extent.
 * @returns The rectangle.
 */
function insideOf(map: MapDocument, side: MapSide, from: number, to: number, depth: number): StripRect {
  const span = to - from;
  const deep = Math.min(depth, extentAcross(map, side));
  switch (side) {
    case "north":
      return { x: from, y: 0, width: span, height: deep };
    case "south":
      return { x: from, y: map.height - deep, width: span, height: deep };
    case "east":
      return { x: map.width - deep, y: from, width: deep, height: span };
    case "west":
      return { x: 0, y: from, width: deep, height: span };
  }
}

/**
 * Whether two rectangles share any area.
 *
 * @param a One rectangle.
 * @param b The other.
 * @returns True when their interiors overlap.
 */
function meets(a: StripRect, b: StripRect): boolean {
  return a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height;
}

/**
 * The strips of a map's placed neighbours, the band on its own side beside each, and the stretches of its
 * sides no neighbour covers. A strip is one chunk deep, or as deep as the neighbour is where it is shallower,
 * and as long as the stretch the two maps share. A map with no place stands alone and has none of these.
 *
 * @param world The world.
 * @param mapId The map in view.
 * @returns The depth, the strips by side and then along it, and the stretches with no map.
 */
export function edgeStrips(world: WorldDocument, mapId: string): EdgeStrips {
  const depth = world.chunkSize;
  const map = world.maps.find((m) => m.id === mapId);
  if (map?.place === undefined) {
    return { depth, strips: [], noMap: [] };
  }
  const own = map.place;
  const groups = new Map((world.mapGroups ?? []).map((g) => [g.id, g.name]));
  const strips: EdgeStrip[] = [];
  for (const found of neighboursOf(world, mapId)) {
    const other = world.maps.find((m) => m.id === found.map);
    if (other?.place === undefined) {
      continue;
    }
    const reach = Math.min(depth, extentAcross(other, opposite(found.side)));
    const outside = outsideOf(map, found.side, found.from, found.to, reach);
    const dx = own.x - other.place.x;
    const dy = own.y - other.place.y;
    strips.push({
      side: found.side,
      map: other.id,
      name: other.name,
      region: other.mapGroup === undefined ? null : (groups.get(other.mapGroup) ?? null),
      state: other.layers.length === 0 ? "blank" : other.draft === true ? "draft" : "ready",
      from: found.from,
      to: found.to,
      outside,
      source: { x: outside.x + dx, y: outside.y + dy, width: outside.width, height: outside.height },
      band: insideOf(map, found.side, found.from, found.to, depth),
    });
  }
  const noMap: NoMapStretch[] = [];
  for (const side of SIDES) {
    let at = 0;
    const length = lengthAlong(map, side);
    for (const strip of strips.filter((s) => s.side === side)) {
      if (strip.from > at) {
        noMap.push({ side, from: at, to: strip.from, outside: outsideOf(map, side, at, strip.from, depth) });
      }
      at = Math.max(at, strip.to);
    }
    if (at < length) {
      noMap.push({ side, from: at, to: length, outside: outsideOf(map, side, at, length, depth) });
    }
  }
  return { depth, strips, noMap };
}

/**
 * The strips and marks the view reaches: a strip stays when its strip or its band is in the visible rectangle,
 * a mark when its stretch is, so a side out of view costs nothing to draw.
 *
 * @param set The strips of a map.
 * @param view The visible rectangle in tiles.
 * @returns The strips and marks in view.
 */
export function edgeStripsInView(set: EdgeStrips, view: StripRect): EdgeStrips {
  return {
    depth: set.depth,
    strips: set.strips.filter((s) => meets(s.outside, view) || meets(s.band, view)),
    noMap: set.noMap.filter((n) => meets(n.outside, view)),
  };
}

/**
 * The neighbour's strip a point lies in.
 *
 * @param set The strips of a map.
 * @param x The point's x in tiles.
 * @param y The point's y in tiles.
 * @returns The strip, or null when the point is in none (inside the map, past a strip, or on a mark).
 */
export function edgeStripAt(set: EdgeStrips, x: number, y: number): EdgeStrip | null {
  return set.strips.find((s) => x >= s.outside.x && x < s.outside.x + s.outside.width && y >= s.outside.y && y < s.outside.y + s.outside.height) ?? null;
}

/**
 * Where Go to neighbour lands for a side: on the facing edge of the map that shares that stretch of it, at the
 * same row or column. A position outside every shared stretch of the side lands on the nearest end of one.
 *
 * @param world The world.
 * @param mapId The map in view.
 * @param side The side to go through.
 * @param along The position along that side, in the map's own tiles (its x for north and south, its y for east
 *   and west), usually the view's centre.
 * @returns The neighbour and the tile on its facing edge, or null when the side has no neighbour.
 */
export function neighbourToGo(world: WorldDocument, mapId: string, side: MapSide, along: number): NeighbourTarget | null {
  const map = world.maps.find((m) => m.id === mapId);
  const found = neighboursOf(world, mapId).filter((n) => n.side === side);
  if (map?.place === undefined || found.length === 0) {
    return null;
  }
  const pick = found.reduce((best, n) => {
    const gap = (to: { from: number; to: number }): number => (along < to.from ? to.from - along : along >= to.to ? along - (to.to - 1) : 0);
    return gap(n) < gap(best) ? n : best;
  });
  const other = world.maps.find((m) => m.id === pick.map);
  if (other?.place === undefined) {
    return null;
  }
  const here = Math.min(Math.max(Math.floor(along), pick.from), pick.to - 1);
  const shift = side === "north" || side === "south" ? map.place.x - other.place.x : map.place.y - other.place.y;
  const there = here + shift;
  switch (side) {
    case "north":
      return { map: other.id, side: "south", x: there, y: other.height };
    case "south":
      return { map: other.id, side: "north", x: there, y: 0 };
    case "east":
      return { map: other.id, side: "west", x: 0, y: there };
    case "west":
      return { map: other.id, side: "east", x: other.width, y: there };
  }
}

/**
 * One chunk of a strip: where it sits in the map's own chunks (outside the map, so possibly negative or past
 * the last) and which chunk of the neighbour it shows.
 */
export interface StripChunk {
  readonly cx: number;
  readonly cy: number;
  readonly sx: number;
  readonly sy: number;
}

/**
 * The chunks of a strip, one row or column of them, in the map's own chunk numbering with the neighbour's
 * chunk each shows. Places are whole chunks apart, so the offset between the two maps is a whole number of
 * chunks. With a view only the chunks it reaches are listed, so a long edge draws no more than is in view.
 *
 * @param strip One strip of `edgeStrips`.
 * @param chunkSize The world's chunk size in tiles.
 * @param view The visible rectangle in tiles, or undefined for every chunk of the strip.
 * @returns The chunks, by row and then column.
 */
export function stripChunks(strip: EdgeStrip, chunkSize: number, view?: StripRect): StripChunk[] {
  const out = strip.outside;
  const shiftX = Math.round((strip.source.x - out.x) / chunkSize);
  const shiftY = Math.round((strip.source.y - out.y) / chunkSize);
  const chunks: StripChunk[] = [];
  for (let cy = Math.floor(out.y / chunkSize); cy <= Math.floor((out.y + out.height - 1) / chunkSize); cy += 1) {
    for (let cx = Math.floor(out.x / chunkSize); cx <= Math.floor((out.x + out.width - 1) / chunkSize); cx += 1) {
      if (view === undefined || meets({ x: cx * chunkSize, y: cy * chunkSize, width: chunkSize, height: chunkSize }, view)) {
        chunks.push({ cx, cy, sx: cx + shiftX, sy: cy + shiftY });
      }
    }
  }
  return chunks;
}
